import { haveSameElements } from "app/pages/sales-intelligence/utils/arrays";
import type { IChipItem } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";

import { DUMMY_PROFILE_NAME, UNSAVED_PROFILE_ID } from "../constants/common";
import type { Profile } from "../types/common";
import { ProfileAccessLevel, type ProfileDto } from "../types/dto";

/**
 * Predicate function to check whether given profile is a saved profile
 * @param profile
 */
export function isSavedProfile(profile: Profile) {
    return profile.id !== UNSAVED_PROFILE_ID;
}

/**
 * Predicate function to check whether given profile is not a saved profile
 * @param profile
 */
export function isUnsavedProfile(profile: Profile) {
    return !isSavedProfile(profile);
}

/**
 * Checks whether given `profile` has at least some value selected in either of the lists
 * @param profile
 */
export function isProfileNotEmpty<P extends ProfileDto>(profile: P) {
    return !isProfileEmpty(profile);
}

/**
 * Checks whether given `profile` doesn't have any value selected in any of the lists
 * @param profile
 */
export function isProfileEmpty<P extends ProfileDto>(profile: P) {
    return (
        profile.seniority.length === 0 &&
        profile.departments.length === 0 &&
        profile.includeTitles.length === 0 &&
        profile.excludeTitles.length === 0
    );
}

/**
 * Checks whether given `profile` is currently enabled
 * @param profile
 */
export function isProfileEnabled(profile: Profile) {
    return profile.isEnabled;
}

/**
 * Creates empty employee profile object with given `name`
 * @param name
 * @param isExpanded
 */
export function createEmptyProfile(name: string, isExpanded = true): Profile {
    return {
        name,
        isExpanded,
        isEnabled: true,
        isShared: false,
        id: UNSAVED_PROFILE_ID,
        excludeTitles: [],
        includeTitles: [],
        departments: [],
        seniority: [],
        accessLevel: ProfileAccessLevel.User,
    };
}

/**
 * Crates pre-defined name for profile with given number put at the end
 * @param index
 * @example
 * ```
 * createDummyProfileName(1) -> "Profile 1"
 * createDummyProfileName(2) -> "Profile 2"
 * ```
 */
function createDummyProfileName(index: number) {
    if (index <= 0) {
        return `${DUMMY_PROFILE_NAME} 1`;
    }

    return `${DUMMY_PROFILE_NAME} ${index}`;
}

/**
 * Generates dummy name for the next profile based on existing profiles
 * @param profiles
 */
export function generateDummyNameForNextProfile(profiles: readonly Profile[]) {
    return createDummyProfileName(profiles.length + 1);
}

/**
 * Compares given profiles DTOs
 * @param profileA
 * @param profileB
 */
export function compareTwoProfilesDTOsEqual<P extends Omit<ProfileDto, "id" | "isShared">>(profileA: P, profileB: P) {
    if (profileA.name !== profileB.name) {
        return false;
    }

    for (const key of ["departments", "seniority", "includeTitles", "excludeTitles"]) {
        if (!haveSameElements(profileA[key], profileB[key])) {
            return false;
        }
    }

    return true;
}

function getToUIProfileMapper(isEnabled = false, isExpanded = false) {
    return function toUIProfileMapper(dto: ProfileDto): Profile {
        return {
            ...dto,
            isEnabled,
            isExpanded,
            seniority: dto.seniority ?? [],
            departments: dto.departments ?? [],
            includeTitles: dto.includeTitles ?? [],
            excludeTitles: dto.excludeTitles ?? [],
        };
    };
}

/**
 * Maps given profile DTOs array to UI profiles objects
 * @param profilesDTOs
 */
export function getUIProfilesFromDTOs(profilesDTOs: readonly ProfileDto[]): readonly Profile[] {
    if (profilesDTOs.length === 0) {
        return [];
    }

    if (profilesDTOs.length === 1) {
        const [profile] = profilesDTOs;

        if (isProfileEmpty(profile)) {
            return [getToUIProfileMapper(true, true)(profile)];
        }
    }

    return profilesDTOs.map(getToUIProfileMapper());
}

/**
 * Joins given list of strings with given `separator` respecting given `lengthLimit`
 * @param values
 * @param lengthLimit
 * @param separator
 */
export function getSeparatedValuesPlusN(values: readonly string[], lengthLimit: number, separator = ", ") {
    let selectedValuesAsString = values.join(separator);
    let numberOfElse = 0;

    while (selectedValuesAsString.length > lengthLimit) {
        numberOfElse++;
        selectedValuesAsString = values.slice(0, -numberOfElse).join(separator);
    }

    return `${selectedValuesAsString}${numberOfElse > 0 ? ` + ${numberOfElse}` : ""}`;
}

export function mapStringToChipItem(string: string): IChipItem {
    return {
        id: string,
        text: string,
    };
}
